/**
 * Pursuit record sections (20-growth.md §3.3, GRW-15): the slugs under
 * `/capture/:id/`, the legacy `?tab=` mapping in both directions, and the
 * redirect rule PursuitRecordLayout applies. The component per slug lives in
 * pursuitSectionComponents.ts so these maps stay importable without the panels.
 */ /** Every routed section slug ('' = Home). `customer/themes` sits before `customer` for the router. */ export const PURSUIT_SECTION_SLUGS = [
    '',
    'chat',
    'customer/themes',
    'customer',
    'competitors',
    'black-hat',
    'win-themes',
    'teaming',
    'rfi-responses',
    'proposal',
    'decisions',
    'surgery',
    'documents',
    'plan',
    'solution',
    'outputs',
    'past-performance',
    'workstreams',
    'graph'
];
/** `/capture/:id/<slug>`, typed so a renamed slug breaks the build, not the link. */ export function pursuitSectionPath(id, slug) {
    return slug ? `/capture/${id}/${slug}` : `/capture/${id}`;
}
/** The nine tabs of today's CaptureDetailPage. */ export const LEGACY_PURSUIT_TABS = [
    'overview',
    'rfis',
    'interactions',
    'themes',
    'workstreams',
    'documents',
    'past-performance',
    'solution',
    'outputs'
];
const TAB_TO_SLUG = {
    overview: 'plan',
    rfis: 'rfi-responses',
    interactions: 'customer',
    themes: 'customer/themes',
    workstreams: 'workstreams',
    documents: 'documents',
    'past-performance': 'past-performance',
    solution: 'solution',
    outputs: 'outputs'
};
const SLUG_TO_TAB = {
    ...Object.fromEntries(Object.entries(TAB_TO_SLUG).map(([tab, slug])=>[
            slug,
            tab
        ])),
    'black-hat': 'outputs',
    'win-themes': 'outputs'
};
/** Flag on, `?tab=X` → the section slug. Unknown tabs → Home (today's page ignores them too). */ export function legacyTabToSlug(tab) {
    return TAB_TO_SLUG[tab] ?? '';
}
/** Flag off, nested URL → the tab of today's page that shows it; every other slug → overview. */ export function slugToLegacyTab(slug) {
    return SLUG_TO_TAB[slug] ?? 'overview';
}
function withQuery(path, params) {
    const query = params.toString();
    return query ? `${path}?${query}` : path;
}
/**
 * Where PursuitRecordLayout must send the browser (always `replace`), or null.
 * `rest` is the path after `/capture/:id` with no leading slash ('' = Home).
 * Every query param other than the ones consumed here is kept (outputOwner,
 * solutionRevision, solutionTarget, returnTo, logTouchpoint, contact, …).
 */ export function pursuitRedirect(opts) {
    const { enabled, id, rest } = opts;
    const base = `/capture/${id}`;
    const params = new URLSearchParams(opts.search);
    if (!enabled) {
        // Links minted by new code keep working where the shell is off: show the matching tab.
        if (rest === '') return null;
        params.set('tab', slugToLegacyTab(rest));
        return withQuery(base, params);
    }
    const tab = params.get('tab');
    if (tab !== null) {
        params.delete('tab');
        const slug = legacyTabToSlug(tab);
        return withQuery(slug ? `${base}/${slug}` : base, params);
    }
    if (rest === '') {
        const kind = params.get('overviewKind');
        if (kind === null) return null;
        const overviewId = params.get('overviewId') ?? '';
        params.delete('overviewKind');
        params.delete('overviewId');
        if (kind === 'decision') {
            params.set('decisionId', overviewId);
            return withQuery(`${base}/decisions`, params);
        }
        return withQuery(`${base}/plan`, params);
    }
    // Only Home hosts Log touchpoint (GRW-16): carry the deep link there.
    if (params.get('logTouchpoint') === '1') return withQuery(base, params);
    return null;
}
